import { normalizeMarkdownText } from "./normalizeStopLossText.js";

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function inlineHtml(value) {
    let text = escapeHtml(value);
    text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');
    text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");
    text = text.replace(/(^|[\s(])\*([^*\n]+)\*/g, "$1<em>$2</em>");
    text = text.replace(/(^|[\s(])_([^_\n]+)_/g, "$1<em>$2</em>");
    return text;
}

function isContingencyHeading(line) {
    const text = String(line || "").replace(/^#{1,6}\s*/, "").trim();
    if (text.length < 3 || text.length > 80) return false;
    if (/^#{1,6}\s+\S/.test(line)) return true;
    if (/^contingenc(?:y|ies)$/i.test(text)) return true;
    const letters = text.replace(/[^A-Za-z]/g, "");
    return letters.length >= 3 && letters === letters.toUpperCase();
}

function cellsOf(line) {
    let text = String(line || "").trim();
    if (!text.includes("|")) return null;
    if (text.startsWith("|")) text = text.slice(1);
    if (text.endsWith("|")) text = text.slice(0, -1);
    return text.split("|").map((cell) => cell.trim());
}

function isSeparator(line) {
    const cells = cellsOf(line);
    if (!cells || cells.length < 2) return false;
    return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function isTableRow(line) {
    const text = String(line || "").trim();
    if (!isSeparator(text) && cellsOf(text)) return true;
    return false;
}

function tableHtml(rows) {
    const width = Math.max(...rows.map((row) => row.length));
    const [header, ...body] = rows;
    const cells = (row, tag) => {
        const padded = [...row];
        while (padded.length < width) padded.push("");
        return padded.map((cell) => `<${tag}>${inlineHtml(cell)}</${tag}>`).join("");
    };
    const bodyHtml = body.map((row) => `<tr>${cells(row, "td")}</tr>`).join("");
    return `<table><thead><tr>${cells(header, "th")}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
}

export function contingenciesHtml(value) {
    const lines = normalizeMarkdownText(value).split("\n");
    const html = [];
    let paragraph = [];
    let list = null;
    const flushParagraph = () => {
        if (!paragraph.length) return;
        html.push(`<p>${paragraph.map((line) => inlineHtml(line)).join("<br>")}</p>`);
        paragraph = [];
    };
    const flushList = () => {
        if (!list?.items.length) {
            list = null;
            return;
        }
        html.push(`<${list.tag}>${list.items.map((item) => `<li>${inlineHtml(item)}</li>`).join("")}</${list.tag}>`);
        list = null;
    };
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index].trim();
        if (!line) {
            flushParagraph();
            flushList();
            continue;
        }
        if (isTableRow(line) && isSeparator(lines[index + 1])) {
            flushParagraph();
            flushList();
            const rows = [cellsOf(line)];
            index += 1;
            while (isTableRow(lines[index + 1])) {
                index += 1;
                rows.push(cellsOf(lines[index]));
            }
            html.push(tableHtml(rows));
            continue;
        }
        if (isContingencyHeading(line)) {
            flushParagraph();
            flushList();
            html.push(`<h3>${inlineHtml(line.replace(/^#{1,6}\s*/, ""))}</h3>`);
            continue;
        }
        const bullet = line.match(/^[-•*+]\s+(.*)$/);
        const numbered = line.match(/^\d+[.)]\s+(.*)$/);
        if (bullet || numbered) {
            flushParagraph();
            const tag = bullet ? "ul" : "ol";
            if (!list || list.tag !== tag) {
                flushList();
                list = { tag, items: [] };
            }
            list.items.push((bullet || numbered)[1]);
            continue;
        }
        flushList();
        paragraph.push(line);
    }
    flushParagraph();
    flushList();
    return html.join("");
}
